import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loadable from "@react-loadable/revised";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { TreeView } from "@components_v2/tree-view";
import FolderTreeViewSkeleton from "@components/tree/components/folder-tree-view-skeleton";
import FolderTreeViewItem from "@components/tree/components/folder-tree-view-item";
import FolderDragPreview from "@components/tree/components/folder-drag-preview";
import ReactDOM from "react-dom";
import { archiveAssetAPI } from "@modules/apis/assets";
import DialogArchive from "@features/dialog/dialog-archive";
import DialogArchiveRestore from "@features/dialog/dialog-archive-restore";
import DialogAssetDuplicate from "@routes/asset/dialog-asset-folder-duplicate";
import {
    attachMovedFolder,
    deleteFolder,
    detachMovedFolder,
    endFolderMove,
    getFoldersList,
    initFolderData,
    moveFolder,
    startFolderMove,
    updateFolderInfo,
    updateMovedFolder,
} from "@modules/folderTreeView";
import { DRIVE_FOLDER_SEARCH_TYPE } from "@constants";
import { setNotification } from "@modules/notification";
import { siteMode } from "@cores/siteMode";
import menuService from "@services/menuService";
import { listJobs } from "@modules/bulkJobs";
import useCheckPermission from "@hooks/useCheckPermission";
import useArchive from "@hooks/useArchive";
import DialogCreateCollection from "@routes/collections/dialog-create";
import compareVersions, {
    VERSION_CM_11210_ASSET_DUPLICATION,
    VERSION_CM_12200_CREATE_COLLECTION_WITH_ASSET_FOLDER,
    VERSION_CM_12290_COLLECTION_ARCHIVE,
} from "@cores/version";
import useDownloadJobs from "@hooks/useDownloadJobs";
import useFolder from "@hooks/useFolder";
import useAbortController from "@hooks/useAbortController";

const LoadableCreateFolderModal = Loadable({
    loader: () => import("../modals/NewFolderModal"),
    loading: () => null,
});

const LoadableDeleteFolderModal = Loadable({
    loader: () => import("../../routes/asset/ui/DeleteFolderConfirm"),
    loading: () => null,
});

const LoadableSelectFolderPathModal = Loadable({
    loader: () => import("../modals/SelectFolderPath"),
    loading: () => null,
});

const LoadableFolderRenameModal = Loadable({
    loader: () => import("../modals/FolderRenameModal"),
    loading: () => null,
});
const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const FolderTreeView = ({
    open,
    domain,
    driveInfo,
    resourceType,
    isModal,
    driveList,
    folderList,
    onHandleCreateFolder,
    onClickForderTree,
    selectedFolder,
    onRefresh,
    useActionMenus = false,
    isCustomMenu,
    isCms,
    onDropTreeItem,
    isFolderDndEnabled = false,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const { spaceId, projectId } = menuService.parse(location.pathname);

    const FOLDERS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Folders" });
    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const { requestArchive } = useArchive();
    const { requestDownloadJob } = useDownloadJobs();
    const { onRenameFolder } = useFolder({});
    const { getNewController, abortAll } = useAbortController();

    const { project, folderTreeViewModal, stage, folderActionPending } = useSelector(
        (store) => ({
            project: store.project,
            folderTreeViewModal: store.folderTreeViewModal,
            stage: store.stage,
            folderActionPending: store.folderTreeView.actionPending,
        }),
        shallowEqual,
    );

    // 이동 액션이 진행 중인 폴더 — 해당 행의 드래그·드롭을 잠근다 (모달 이동은 별개 상태라 간섭하지 않음)
    const movePendingFolderIds = useMemo(
        () => folderActionPending.filter((entry) => entry.type === "move").map((entry) => entry.folderId),
        [folderActionPending],
    );

    const draggingFolderIdRef = useRef(null);
    const folderDragPreviewRef = useRef(null);
    // 현재 드롭 강조 중인 행 — { folderId, clear }. 강조를 켠 행이 스스로 등록하고 다음 행이 이전 행을 끈다
    const activeDropRowRef = useRef(null);

    // 드래그 종료 시 남은 강조 일괄 정리.
    // dragend는 드래그 소스 행에서만 발생하고 dragleave는 이탈할 때만 오므로, 행 단위 정리만으로는
    // 취소(ESC)·트리 밖 드롭·낙관 반영으로 소스 행이 언마운트돼 dragend가 유실되는 경로에서 강조가 남는다.
    // 반드시 bubble 단계 — capture로 받으면 행의 onDrop보다 먼저 실행돼 강조를 끄고, 그 setState가
    // React 밖 리스너라 동기 리렌더를 일으켜 onDrop이 dropPosition을 null로 읽는다(= 폴더 이동이 조용히 무산).
    // 행이 드롭을 처리한 경우엔 handleDrop이 스스로 강조를 거두고 stopPropagation 하므로 여기까지 오지 않는다.
    // 폴더 DnD 여부와 무관하게 등록한다 — 에셋 등 목록 아이템 드롭도 같은 into 강조를 사용한다.
    // draggingFolderIdRef는 건드리지 않는다 — 여기서 비우면 뒤이어 실행될 행의 drop이 무효 대상으로 판정된다.
    useEffect(() => {
        const clearActiveDropRow = () => {
            activeDropRowRef.current?.clear();
            activeDropRowRef.current = null;
        };
        window.addEventListener("dragend", clearActiveDropRow);
        window.addEventListener("drop", clearActiveDropRow);
        return () => {
            window.removeEventListener("dragend", clearActiveDropRow);
            window.removeEventListener("drop", clearActiveDropRow);
        };
    }, []);

    const [dialog, setDialog] = useState("");
    const [selectedFolderByMenu, setSelectedFolderByMenu] = useState(null);
    const [createdFolderId, setCreatedFolderId] = useState("");
    const [actionPending, setActionPending] = useState({});
    // 이동 완료된 폴더의 스크롤·하이라이트 대상 — 순수 뷰 관심사라 로컬 state로 관리
    const [revealedFolderId, setRevealedFolderId] = useState(null);
    const revealClearTimerRef = useRef(null);

    // 행이 실제로 스크롤·하이라이트된 시점부터 2초 유지 — 드레인 진행 중 이동은 완료 후 행 마운트 시 발화되므로 설정 시점 기준이면 유실됨
    const onRevealedFolder = useCallback(() => {
        clearTimeout(revealClearTimerRef.current);
        revealClearTimerRef.current = setTimeout(() => setRevealedFolderId(null), 2000); // CSS 애니메이션(1.6s)보다 길게 유지 후 해제
    }, []);

    useEffect(() => {
        if (!revealedFolderId) return;
        // 행이 끝내 마운트되지 않는 경우(조회 실패 등) 대비 상한 — 만료 전 발화 시 onRevealedFolder가 2초로 재설정
        revealClearTimerRef.current = setTimeout(() => setRevealedFolderId(null), 10000);
        return () => clearTimeout(revealClearTimerRef.current);
    }, [revealedFolderId]);

    //언마운트 시 이 컴포넌트가 띄운 폴더 조회 취소 (삭제·이동 후 재조회 포함)
    useEffect(() => {
        return () => {
            abortAll();
        };
    }, [abortAll]);

    const onClickResetFolderTreeFilter = async (driveId, rootFolder) => {
        onClickForderTree(rootFolder);
    };

    const onHandleClickCloseDialog = () => {
        setDialog("");
        setSelectedFolderByMenu(null);
    };

    const drive = useMemo(() => {
        if (!driveList.data) return {};
        return driveList.data?.find((el) => el.resourceType?.type === domain.toUpperCase());
    }, [driveList, domain]);

    const onHandleClickDownloadFolder = useCallback(
        (domain, folder) => {
            const downloadRequestTargets = {
                virtualFolder: { id: folder.id, recursive: false },
                projectId: projectId,
                resourceName: folder.name,
            };
            requestDownloadJob(domain.toUpperCase(), downloadRequestTargets, 1, false);
        },
        [requestDownloadJob],
    );

    const getTreeActionsMenu = useCallback(
        (folderObj) => {
            if (isModal || siteMode.get() === "CMS" || !useActionMenus) return undefined;

            switch (resourceType) {
                case "Asset":
                    return [
                        {
                            label: t(`common::label::New Folder`),
                            id: "create",
                            clickClose: true,
                            onClick: () => {
                                setDialog("create");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Move`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("move");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Rename`),
                            id: "rename",
                            clickClose: true,
                            onClick: () => {
                                setDialog("rename");
                                setSelectedFolderByMenu({
                                    ...folderObj,
                                    driveInfo: drive,
                                    parentInfo: folderObj.parent ? folderList.data?.[folderObj.parent] : {},
                                });
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Duplicate`),
                            id: "duplicate",
                            clickClose: true,
                            hidden: compareVersions(stage.version, VERSION_CM_11210_ASSET_DUPLICATION) < 0,
                            onClick: () => {
                                setDialog("duplicate");
                                setSelectedFolderByMenu({
                                    ...folderObj,
                                    driveInfo: drive,
                                    parentInfo: folderObj.parent ? folderList.data?.[folderObj.parent] : {},
                                });
                            },
                            disabled: !ASSETS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Delete`),
                            id: "delete",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("delete");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Create Collection`),
                            id: "createCollection",
                            clickClose: true,
                            divider: true,
                            hidden:
                                compareVersions(stage.version, VERSION_CM_12200_CREATE_COLLECTION_WITH_ASSET_FOLDER) <
                                0,
                            onClick: () => {
                                setDialog("createCollection");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Archive`),
                            id: "archive",
                            clickClose: true,
                            divider: true,
                            disabled: !ASSETS_PERMISSIONS.ARCHIVE,
                            onClick: () => {
                                setDialog("archive");
                                setSelectedFolderByMenu(folderObj);
                            },
                        },
                    ];
                case "Collection":
                    return [
                        {
                            label: t(`common::label::New Folder`),
                            id: "create",
                            clickClose: true,
                            onClick: () => {
                                setDialog("create");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Share link`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("shareLink");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !SHARES_PERMISSIONS.EXTERNAL,
                        },
                        {
                            label: t(`common::label::Move`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("move");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Rename`),
                            id: "rename",
                            clickClose: true,
                            onClick: () => {
                                setDialog("rename");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Download`),
                            id: "download",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                onHandleClickDownloadFolder(domain, folderObj);
                            },
                            disabled: !DOWNLOAD_PERMISSIONS.DOWNLOAD,
                        },
                        {
                            label: t(`common::label::Delete`),
                            id: "delete",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("delete");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Archive`),
                            id: "archive",
                            clickClose: true,
                            divider: true,
                            hidden: compareVersions(stage.version, VERSION_CM_12290_COLLECTION_ARCHIVE) < 0,
                            onClick: () => {
                                setDialog("archive");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.ARCHIVE,
                        },
                    ];
                case "Archived":
                    return [
                        {
                            label: t(`common::label::Restore`),
                            id: "restore",
                            clickClose: true,
                            onClick: () => {
                                setDialog("restore");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.ARCHIVE,
                        },
                    ];
                case "CustomContent":
                    return [
                        {
                            label: t(`common::label::New Folder`),
                            id: "create",
                            clickClose: true,
                            onClick: () => {
                                setDialog("create");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Move`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("move");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Rename`),
                            id: "rename",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("rename");
                                setSelectedFolderByMenu({
                                    ...folderObj,
                                    driveInfo: drive,
                                    parentInfo: folderObj.parent ? folderList.data?.[folderObj.parent] : {},
                                });
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Delete`),
                            id: "delete",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("delete");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                    ];
            }
        },
        [
            resourceType,
            driveInfo,
            stage,
            isModal,
            driveList,
            folderList,
            drive,
            ASSETS_PERMISSIONS,
            FOLDERS_PERMISSIONS,
            FOLDERS_PERMISSIONS,
            DOWNLOAD_PERMISSIONS,
            requestDownloadJob,
        ],
    );

    const onHandleClickFolder = useCallback(
        async (folder, isClickArrow, options) => {
            if (actionPending?.[folder.id]) return;

            setActionPending({ ...actionPending, [folder.id]: true });
            try {
                await onClickForderTree(folder, isClickArrow, options);
            } finally {
                setActionPending({ ...actionPending, [folder.id]: false });
            }
        },
        [actionPending],
    );

    const selectedVirtualFolderId = useMemo(
        () => selectedFolder ?? new URLSearchParams(location.search).get("virtualFolderId"),
        [location.search, selectedFolder],
    );

    // plan은 use-tree-item-dnd의 resolveFolderDropPlan이 드롭 시점에 해석해 전달 (무효/no-op 드롭은 여기까지 오지 않음)
    const onDropFolderItem = useCallback(
        async (plan) => {
            const { draggedFolder, toFolder, originalPosition, isReorder, isDeferredReveal, moveParams } = plan;
            // 이 폴더의 이동 액션 시작 — 낙관 반영보다 먼저 등록해야 그 순간부터 행이 잠긴다.
            // 진행 중이던 대상 조회가 이 엔트리를 소비하면 이동 폴더 표시와 잠금 해제가 함께 일어난다
            dispatch(startFolderMove({ parentId: toFolder.id, folderId: draggedFolder.id }));

            if (isDeferredReveal) {
                // 접힌·조회 중 대상 — 원위치 제거만 하고 move 성공 후 attachMovedFolder로 표시
                dispatch(detachMovedFolder({ folderId: draggedFolder.id }));
            } else {
                // 낙관적 이동 — 드롭 즉시 반영하고 실패 시 역방향으로 원복 (into는 moveParams.position이 없어 undefined=맨 뒤)
                dispatch(
                    updateMovedFolder({
                        fromPosition: { id: draggedFolder.id, parent: { id: originalPosition.parentId } },
                        toPosition: { id: toFolder.id, parent: { id: toFolder.parent } },
                        previousId: moveParams.position?.previousId,
                    }),
                );
            }

            try {
                await dispatch(moveFolder({ driveId: draggedFolder.driveId }, moveParams));
                if (isDeferredReveal) {
                    // move 응답 시점 대상 상태에 따라 직접 삽입 또는 대기 등록(+접힘이면 조회)으로 이동 폴더 표시.
                    // 진행 중 조회에 맡기는 경우는 등록 후 즉시 반환되므로 토스트가 표시보다 앞설 수 있음
                    await dispatch(
                        attachMovedFolder({
                            driveId: draggedFolder.driveId,
                            folderId: draggedFolder.id,
                            parentId: toFolder.id,
                            headerParams: { projectId },
                            signal: getNewController(`folderChildren:${toFolder.id}`),
                        }),
                    );
                } else {
                    // 낙관 반영은 이미 화면에 있어 소비할 조회가 없다 — 여기서 액션 종료
                    dispatch(endFolderMove(draggedFolder.id));
                }
                if (!isReorder) {
                    dispatch(
                        setNotification({
                            type: "success",
                            contents: t(
                                `common::msg::Moved '{{item1}}' into '{{item2}}'.`,
                                `Moved '{{item1}}' into '{{item2}}'.`,
                                { item1: draggedFolder.name, item2: toFolder.name },
                            ),
                        }),
                    );
                }
                setRevealedFolderId(draggedFolder.id);
            } catch (error) {
                // 원복하므로 대기 액션도 함께 종료 — 남기면 진행 중이던 조회가 원복된 폴더를 대상에 다시 붙인다
                dispatch(endFolderMove(draggedFolder.id));
                dispatch(
                    updateMovedFolder({
                        fromPosition: { id: draggedFolder.id, parent: { id: toFolder.id } },
                        toPosition: { id: originalPosition.parentId, parent: { id: originalPosition.grandParentId } },
                        previousId: originalPosition.previousId,
                    }),
                );
                const failedAction = isReorder ? "order change" : "move";
                const failedMessage = t(
                    `common::msg::Failed to save the folder ${failedAction}. Reverted to the original state.`,
                    `Failed to save the folder ${failedAction}. Reverted to the original state.`,
                );
                // 이름 충돌·depth 초과 등 실패 원인은 서버 메시지에만 담겨 오므로 실패 문구 다음 줄에 노출.
                // 문자열이 아니면 버린다 — React 자식으로 직접 렌더해 객체가 오면 렌더 중 throw된다
                const rawMessage = error?.response?.data?.message;
                const serverMessage = typeof rawMessage === "string" ? rawMessage : "";
                dispatch(
                    setNotification({
                        type: "error",
                        contents: serverMessage ? (
                            <>
                                {failedMessage}
                                <br />
                                {serverMessage}
                            </>
                        ) : (
                            failedMessage
                        ),
                    }),
                );
            }
        },
        [dispatch, t, projectId, getNewController],
    );

    const renderTree = useCallback(
        (parentFolderId, children = []) => {
            if (folderList.pending || children.length === 0) return;
            return (
                <React.Fragment key={`folder-tree-view-item-${parentFolderId}-children-container`}>
                    {children.map((folder) => {
                        const folderObj = folderList.data[folder];
                        if (!folderObj) return null;
                        return (
                            <FolderTreeViewItem
                                key={`folder-tree-view-item-${folderObj.id}`}
                                folder={folderObj}
                                onClickFolder={onHandleClickFolder}
                                onClickCreateFolder={onHandleCreateFolder}
                                menuList={getTreeActionsMenu(folderObj)}
                                creatingParentFolder={
                                    isModal ? folderTreeViewModal.folderList?.creatingParentFolder : undefined
                                }
                                onToggleFolderFold={(folderId) => onHandleClickFolder(folderId, true)}
                                renderTree={renderTree}
                                onDropTreeItem={isModal ? undefined : onDropTreeItem}
                                selectedFolderId={selectedVirtualFolderId}
                                canEdit={FOLDERS_PERMISSIONS.EDIT}
                                folderMap={folderList.data}
                                onDropFolderItem={isFolderDndEnabled ? onDropFolderItem : undefined}
                                draggingFolderIdRef={draggingFolderIdRef}
                                dragPreviewRef={isFolderDndEnabled ? folderDragPreviewRef : undefined}
                                movePendingFolderIds={movePendingFolderIds}
                                activeDropRowRef={activeDropRowRef}
                                revealedFolderId={revealedFolderId}
                                onRevealed={onRevealedFolder}
                            />
                        );
                    })}
                </React.Fragment>
            );
        },
        [
            createdFolderId,
            folderList,
            folderTreeViewModal,
            onHandleCreateFolder,
            onHandleClickFolder,
            onDropTreeItem,
            selectedVirtualFolderId,
            isModal,
            FOLDERS_PERMISSIONS.EDIT,
            isFolderDndEnabled,
            onDropFolderItem,
            movePendingFolderIds,
            revealedFolderId,
            onRevealedFolder,
        ],
    );

    const onChangeFolderPath = useCallback(async () => {
        const toFolder = folderTreeViewModal.folderList.selectedFolder.folder;
        const fromObj = { id: selectedFolderByMenu?.id, parent: { id: selectedFolderByMenu?.parent } };
        const toObj = { id: toFolder.id, parent: { id: toFolder.parent } };
        const params = { from: fromObj, to: toObj };
        const driveId = { driveId: selectedFolderByMenu?.driveId };

        try {
            if (toFolder.id !== fromObj.id) {
                await dispatch(moveFolder(driveId, params));

                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(
                            `common::msg::Move completed from {{item1}} to {{item2}}.`,
                            `Move completed from {{item1}} to {{item2}}.`,
                            {
                                item1: folderList.data[fromObj.parent.id].name,
                                item2: folderTreeViewModal.folderList.data[toObj.id].name,
                            },
                        ),
                    }),
                );
            }

            if (!(folderList.selectedFolder.folder.id === fromObj.id && !folderList.data[toFolder.id])) {
                await dispatch(updateMovedFolder({ fromPosition: fromObj, toPosition: toObj }));
            } else {
                //Note: 현재 선택한 폴더를 이동하는데 트리뷰에 아직 정보가 없는 폴더의 경우 루트폴더로 이동
                const rootFolder = folderList?.data?.[drive.rootFolder.id];
                onClickForderTree(rootFolder);
            }

            onHandleClickCloseDialog();
        } catch (error) {
            const errorData = error && error.response && error.response.data;
            const errorMessage = errorData && errorData.message;
            const errorStatus = (error && error.response && error.response.status) || "Unknown";
            const errorContents = `- ${errorMessage}`;

            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(
                            `common::msg::Error moving from {{item1}} to {{item2}}.`,
                            `Error moving from {{item1}} to {{item2}}.`,
                            {
                                item1: folderList.data[fromObj.parent.id].name,
                                item2: folderTreeViewModal.folderList.data[toObj.id].name,
                            },
                        ) + errorContents,
                }),
            );
        }
    }, [folderList, driveList, folderTreeViewModal, drive]);

    const onClickFolderDelete = useCallback(
        async (item) => {
            try {
                const params = { driveId: item.driveId, folderId: item.id };
                await dispatch(deleteFolder(params));

                const queryParameters = {
                    parentId: item.parent,
                    searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
                };
                const headerParams = { projectId };
                if (item.id === folderList.selectedFolder.folder.id) {
                    const rootFolder = await dispatch(
                        initFolderData({
                            headerParams,
                            domain,
                            resourceTypeId: driveInfo?.resourceTypeId,
                            signal: getNewController("folderTree"),
                        }),
                    );
                    await onClickForderTree(rootFolder, false);
                } else {
                    await dispatch(
                        getFoldersList({
                            driveId: item.driveId,
                            queryParameters,
                            headerParams,
                            signal: getNewController(`folderChildren:${item.parent}`),
                        }),
                    );
                }
                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(
                            `common::msg::Deletion of folder {{item}} has been completed.`,
                            `Deletion of folder {{item}} has been completed.`,
                            { item: item.name },
                        ),
                    }),
                );
                onHandleClickCloseDialog();
            } catch (error) {
                //언마운트로 재조회가 취소된 경우 — 삭제 자체는 성공했고 트리도 사라지므로 알림 없이 종료
                if (error?.code === "ERR_CANCELED" || error?.name === "AbortError") return;
                const errorData = error && error.response && error.response.data;
                const errorMessage = errorData && errorData.message;
                const errorStatus = (error && error.response && error.response.status) || "Unknown";
                const errorContents =
                    `- ` +
                    (errorStatus === 400
                        ? t(`common::msg::This folder contains assets.`, `This folder contains assets.`)
                        : `${errorMessage}`);
                console.error(error);

                dispatch(
                    setNotification({
                        type: "error",
                        contents:
                            t(
                                `common::msg::There was an error deleting folder {{item}}.`,
                                `There was an error deleting folder {{item}}.`,
                                { item: item.name },
                            ) + errorContents,
                    }),
                );
            }
        },
        [folderList, driveList, projectId, driveInfo, domain, getNewController],
    );

    const onClickArchiveAssetFolder = async (_, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });

            const id = selectedFolderByMenu?.id;
            if (!id) throw new Error();

            const targets =
                domain === "Asset"
                    ? {
                          virtualFolder: {
                              id,
                              recursive: false,
                          },
                      }
                    : { collection: { virtualFolder: { id, recursive: false } } };

            const data = {
                archiveClass: option,
                targets,
            };

            const isMulti = false;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
                await onRefresh();
            });
        } catch (e) {
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onHandleClickCloseDialog();
        }
    };

    const rootFolderList = useMemo(() => {
        if (!folderList.data) return null;
        return [Object.values(folderList.data).find((folder) => folder?.depth === 0)];
    }, [folderList.data]);

    return (
        <>
            {driveList.pending || folderList.pending ? (
                <FolderTreeViewSkeleton type={"root"} />
            ) : Object.keys(folderList.data ?? {})?.length > 0 ? (
                rootFolderList.length > 0 ? (
                    <TreeView
                        sx={{
                            width: `100%`,
                            minWidth: 318,
                            opacity: 1,
                            overflowY: `auto`,
                            transition: `opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 200ms`,
                            "::-webkit-scrollbar": {
                                width: "14px",
                                height: "14px",
                            },
                            "::-webkit-scrollbar-track": {
                                background: "transparent",
                            },
                            "::-webkit-scrollbar-thumb": {
                                backgroundColor: "#dce1e5",
                                borderRadius: "8px",
                                backgroundClip: "padding-box",
                                border: "4px solid transparent",
                            },
                        }}
                        defaultExpanded={[rootFolderList[0].id]}
                        defaultSelected={[rootFolderList[0].id]}
                        expanded={
                            // 설명: 폴더 닫혀있는 상태에서 하위 폴더 생성시 부모 폴더를 열면서 하위폴더를 보여줘야하는데 스토리북에 expansion에 대한 처리가 되어있어 여기서 제어가 불가능. expanded에서 전체를 다 관리
                            folderList.data &&
                            Object.values(folderList.data)
                                .filter((folder) => !folder.isFold)
                                .map((folder) => folder.id)
                        }
                        selected={[selectedVirtualFolderId]}
                    >
                        {rootFolderList?.map((folder, index) => {
                            return (
                                <FolderTreeViewItem
                                    key={`folder-tree-view-item-${folder.id}`}
                                    folder={folder}
                                    onClickFolder={onHandleClickFolder}
                                    onRefreshDrive={!isModal ? onClickResetFolderTreeFilter : undefined}
                                    onClickCreateFolder={onHandleCreateFolder}
                                    menuList={
                                        ["Asset", "Collection", "Content"].includes(domain) &&
                                        !isModal &&
                                        siteMode.get() !== "CMS" &&
                                        useActionMenus
                                            ? [
                                                  {
                                                      label: t(`common::label::New Folder`, `New Folder`),
                                                      onClick: () => {
                                                          setDialog("create");
                                                          setSelectedFolderByMenu(folder);
                                                      },
                                                      buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
                                                      clickClose: true,
                                                  },
                                              ]
                                            : undefined
                                    }
                                    creatingParentFolder={
                                        isModal ? folderTreeViewModal.folderList?.creatingParentFolder : undefined
                                    }
                                    onToggleFolderFold={(folder) => onHandleClickFolder(folder, true)}
                                    renderTree={renderTree}
                                    onDropTreeItem={isModal ? undefined : onDropTreeItem}
                                    selectedFolderId={selectedVirtualFolderId}
                                    canEdit={FOLDERS_PERMISSIONS.EDIT}
                                    folderMap={folderList.data}
                                    onDropFolderItem={isFolderDndEnabled ? onDropFolderItem : undefined}
                                    draggingFolderIdRef={draggingFolderIdRef}
                                    dragPreviewRef={isFolderDndEnabled ? folderDragPreviewRef : undefined}
                                    movePendingFolderIds={movePendingFolderIds}
                                    activeDropRowRef={activeDropRowRef}
                                />
                            );
                        })}
                    </TreeView>
                ) : (
                    <div>
                        {t(
                            `common::msg::Please manage your assets by adding the new folder.`,
                            "Please manage your assets by adding the new folder.",
                        )}
                    </div>
                )
            ) : null}

            {isFolderDndEnabled && <FolderDragPreview ref={folderDragPreviewRef} />}

            {dialog === "create" &&
                ReactDOM.createPortal(
                    <LoadableCreateFolderModal
                        folder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onClickSubmit={onHandleCreateFolder}
                    />,
                    document.body,
                )}
            {/* 트리뷰에서 폴더 삭제 모달 */}
            {dialog === "delete" &&
                ReactDOM.createPortal(
                    <LoadableDeleteFolderModal
                        folder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onClickDelete={onClickFolderDelete}
                        onDeleteEnd={() => {}}
                    />,
                    document.body,
                )}
            {/* 트리뷰에서 폴더 이동 모달 */}
            {dialog === "move" &&
                ReactDOM.createPortal(
                    <LoadableSelectFolderPathModal
                        open={true}
                        domain={domain}
                        driveInfo={driveInfo}
                        submitText={`Select`}
                        title={`Move`}
                        onSubmit={onChangeFolderPath}
                        onClose={onHandleClickCloseDialog}
                    />,
                    document.body,
                )}

            {/* 트리뷰에서 폴더 이름변경 모달 */}
            {dialog === "rename" &&
                ReactDOM.createPortal(
                    <LoadableFolderRenameModal
                        folder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onClickSubmit={onRenameFolder}
                    />,
                    document.body,
                )}
            {/* 트리뷰에서 폴터 아카이브 모달  */}
            {dialog === "archive" && (
                <DialogArchive
                    active={true}
                    onClose={onHandleClickCloseDialog}
                    onSubmit={onClickArchiveAssetFolder}
                    actionPending={actionPending.archive}
                    folder={selectedFolderByMenu}
                />
            )}
            {/* 트리뷰에서 폴터 아카이브 복원 모달  */}
            {dialog === "restore" && (
                <DialogArchiveRestore
                    isMulti={true}
                    actionPending={actionPending}
                    onSubmitCallback={onRefresh}
                    onClose={onHandleClickCloseDialog}
                    folders={selectedFolderByMenu ? [selectedFolderByMenu] : []}
                    dataType={"folder"}
                />
            )}
            {dialog === "duplicate" && (
                <DialogAssetDuplicate
                    folder={selectedFolderByMenu}
                    onClickClose={onHandleClickCloseDialog}
                    isCms={isCms}
                    isCustomMenu={isCustomMenu}
                />
            )}
            {dialog === "createCollection" && (
                <DialogCreateCollection
                    onClose={onHandleClickCloseDialog}
                    open={dialog === "createCollection"}
                    assetFolder={selectedFolderByMenu}
                />
            )}
            {dialog === "shareLink" &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={domain.toUpperCase()}
                        selectedFolder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onSubmit={() => {
                            onHandleClickCloseDialog();
                            // afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
        </>
    );
};

export default React.memo(FolderTreeView);
